import React from "react";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import Calculator from "@/components/site/Calculator";
import CptTable from "@/components/site/CptTable";
import RoutingBlock from "@/components/site/RoutingBlock";
import { useCalculatorConfig } from "@/hooks/useSiteData";
import { RPM_SPECIALTIES, OG } from "@/lib/content";

const OwnerRow = ({ q, a, testId }) => (
  <div className="grid sm:grid-cols-[minmax(0,18rem)_1fr] gap-2 sm:gap-8 py-5 border-t border-sage/40" data-testid={testId}>
    <div className="text-sm font-semibold text-spruce">{q}</div>
    <div className="text-sm text-ink/80 leading-relaxed">{a}</div>
  </div>
);

export default function Rpm() {
  const { data: config } = useCalculatorConfig();

  return (
    <>
      <Seo
        title="Remote patient monitoring for chronic-care panels"
        description="RPM is a per-patient-per-month business. Run the arithmetic at your panel size, see the CPT codes and their real requirements, and read what happens when it doesn't work."
        path="/rpm"
        ogImage={OG.rpm}
      />

      {/* Arithmetic-first hero */}
      <Band tone="spruce">
        <Eyebrow>Remote patient monitoring</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6 max-w-3xl">
          RPM is a per-patient-per-month business. So start with the arithmetic.
        </h1>
        <p className="text-bone/85 text-base sm:text-lg max-w-2xl mb-8">
          Whether this works depends on your panel size, how many patients enroll, and how many hit the
          16-day threshold each month. Change any of those below and the estimate changes with them.
        </p>
        <div className="flex flex-wrap gap-2">
          {RPM_SPECIALTIES.map((s) => (
            <span key={s} className="tabular text-xs text-bone border border-sage/50 rounded-[2px] px-3 py-1.5">{s}</span>
          ))}
        </div>
      </Band>

      <Band tone="bone">
        {config && <Calculator config={config} mode="rpm" />}
      </Band>

      {/* CPT codes + requirements */}
      <Band tone="ink">
        <Eyebrow>The codes, with their requirements</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-8 max-w-2xl">
          Every code has a condition attached. We keep them attached.
        </h2>
        <div className="[&_th]:text-bone">
          <CptTable />
        </div>
        <p className="text-sm text-sage mt-5 max-w-2xl leading-relaxed" data-testid="rpm-consent-note">
          Patient consent must be obtained and documented before billing. We capture and store that
          consent as part of enrollment.
        </p>
      </Band>

      {/* The two real objections */}
      <Band tone="bone">
        <Eyebrow>The two objections that actually matter</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-spruce leading-tight mb-2 max-w-2xl">
          Device logistics and staff time.
        </h2>
        <p className="text-base text-ink/80 max-w-2xl mb-8">
          Here is who does what, by name of task.
        </p>
        <div>
          <OwnerRow testId="rpm-own-ship" q="Who ships the devices?" a="We do. Cellular-connected devices go directly to the patient, configured and ready — no app setup, no home Wi-Fi needed." />
          <OwnerRow testId="rpm-own-troubleshoot" q="Who troubleshoots them?" a="We do. Device support runs through our team, not your front desk." />
          <OwnerRow testId="rpm-own-call" q="Who calls the patient who stopped transmitting?" a="We do. When readings drop below the 16-day pace, our staff reaches the patient to get them back on track." />
          <OwnerRow testId="rpm-own-document" q="Who documents the 20 minutes?" a="We do, against 99457 and 99458, with the interactive-communication requirement met and recorded." />
        </div>
      </Band>

      {/* When it doesn't work — honest */}
      <Band tone="spruce">
        <Eyebrow>When it doesn't work</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-6 max-w-2xl">
          Some months don't bill. We'd rather you hear that from us.
        </h2>
        <div className="grid sm:grid-cols-3 gap-8 max-w-4xl">
          {[
            ["Patients disengage", "Some enrolled patients stop taking readings. Below 16 days in the period, 99454 doesn't bill for that month. Our follow-up reduces this — it doesn't erase it."],
            ["Devices fail", "Hardware breaks or batteries die. We replace them, but a gap in transmission can still cost a billing month."],
            ["Months that don't bill", "A share of your enrolled panel will not produce a billable month. The calculator's compliance rate is exactly this — lower it to see a conservative number."],
          ].map(([t, d], i) => (
            <div key={i} className="border-t border-signal/60 pt-5" data-testid={`rpm-honest-${i}`}>
              <h3 className="text-lg font-semibold mb-2">{t}</h3>
              <p className="text-sm text-bone/80 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Band>

      {/* Is this you */}
      <Band tone="bone">
        <Eyebrow>Is this you?</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-spruce leading-tight mb-10 max-w-2xl">
          If RPM isn't your fit, we'll point you the right way.
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <RoutingBlock
            testId="rpm-route-340b"
            title="Most of your revenue is specialty drugs?"
            items={["You're hematology, oncology, nephrology, pulmonary, or rheumatology", "Drug acquisition cost is your largest line", "Panel size matters less than drug spend"]}
            to="/340b"
            cta="Look at 340B instead"
          />
          <RoutingBlock
            testId="rpm-route-mso"
            title="Panel's big enough, but no bandwidth?"
            items={["The math works but you can't add staff", "You don't want RPM landing on your nurses", "You want the enrollment and documentation run for you"]}
            to="/mso"
            cta="See what we handle"
          />
        </div>
      </Band>
    </>
  );
}
